import { ControlledSelectField } from 'components/Form';
import { useRequest } from 'features/request/useRequest';
import type { GlobalLimitedCostData } from 'gql/graphql';
import { ChapterEnum, SortEnumType } from 'gql/graphql';
import { SelectItem } from 'uikit';

import type { RenderItemProps } from '@consta/uikit/__internal__/src/components/SelectComponents/types';
import type React from 'react';
import { memo, useMemo } from 'react';
import type { Control } from 'react-hook-form/dist/types/form';

import { bimcGlobalLimitedCostsForCalculationGql } from '../../bimCalculationLimitedQueries';

import Styles from './BimCreateLimitedNameSelect.module.css';

interface ISelectItem extends GlobalLimitedCostData {
    label: string;
    id: number;
}

const prepareSelectItems = (items: GlobalLimitedCostData[]): ISelectItem[] =>
    items.map((item) => ({
        ...item,
        label: item?.name,
        id: item?.id,
    }));

interface IBimCreateLimitedNameSelectProps {
    control: Control;
    calculationId?: number;
    refTooltip?: React.MutableRefObject<null>;
    onMouseMoveSelect?: () => void;
    onMouseLeaveSelect?: () => void;
    isTooltipHidden?: boolean;
    isAdditionalTransport?: boolean;
}

export const BimCreateLimitedNameSelect = memo((props: IBimCreateLimitedNameSelectProps) => {
    const {
        control,
        calculationId,
        refTooltip,
        onMouseMoveSelect,
        onMouseLeaveSelect,
        isTooltipHidden,
        isAdditionalTransport,
    } = props;

    const variables = useMemo(
        () => ({
            calculationId: Number(calculationId),
            order: [{ section: SortEnumType.Desc, name: SortEnumType.Asc }],
        }),
        [calculationId],
    );

    const [bimcGlobalLimitedCostsData] = useRequest({
        requestId: 'bimcGlobalLimitedCosts',
        query: bimcGlobalLimitedCostsForCalculationGql,
        variables: variables as any,
        withEffect: Boolean(calculationId),
    });

    const selectItems = useMemo(
        () =>
            prepareSelectItems(bimcGlobalLimitedCostsData?.bimcGlobalLimitedCostsForCalculation?.items ?? []).filter(
                ({ chapter }) =>
                    isAdditionalTransport ? chapter === ChapterEnum.Chapter1_7 : chapter !== ChapterEnum.Chapter1_7,
            ),
        [isAdditionalTransport, bimcGlobalLimitedCostsData?.bimcGlobalLimitedCostsForCalculation?.items],
    );

    return (
        <ControlledSelectField
            isTooltipHidden={isTooltipHidden}
            label="Наименование"
            name="globalLimitedCost"
            control={control}
            items={selectItems ?? []}
            required
            refTooltip={refTooltip}
            onMouseMove={onMouseMoveSelect}
            onMouseLeave={onMouseLeaveSelect}
            rules={{
                validate: (validateData) => (validateData?.id ? true : 'Обязательное поле'),
            }}
            renderItem={({ item, active, hovered, onClick, onMouseEnter }: RenderItemProps<ISelectItem>) => (
                <SelectItem
                    label={
                        (
                            <div
                                className={Styles.createSelectOption}
                                role="presentation"
                                onMouseEnter={onMouseEnter}
                                onClick={onClick}
                            >
                                {item.label}
                            </div>
                        ) as any
                    }
                    active={active}
                    hovered={hovered}
                    multiple={false}
                    onClick={onClick}
                    onMouseEnter={onMouseEnter}
                    size="s"
                    indent="normal"
                    disabled={false}
                />
            )}
        />
    );
});

BimCreateLimitedNameSelect.displayName = 'BimCreateLimitedNameSelect';
